import { useCallback, useRef } from 'react';
import { useSingletonPromise } from 'framework/utils';
import type { SyberryTodayTaskId } from 'domain/people/syberryToday/types';

type MarkTaskAsOpened = (taskId: SyberryTodayTaskId) => Promise<void>;

type MarkTaskAsOpenedResult =
  | {
      ok: true;
    }
  | {
      ok: false;
      error: unknown;
    };

type UseSyberryTodayTaskOpenedSyncParams = {
  markTaskAsOpened: MarkTaskAsOpened;
  onMarkTaskAsOpenedError: (error: unknown) => Promise<void>;
};

const useSyberryTodayTaskOpenedSync = ({
  markTaskAsOpened,
  onMarkTaskAsOpenedError,
}: UseSyberryTodayTaskOpenedSyncParams) => {
  const pendingTaskOpenedRequestsRef = useRef<Map<SyberryTodayTaskId, Promise<void>>>(new Map());
  const markTaskAsOpenedOnce = useSingletonPromise<SyberryTodayTaskId, MarkTaskAsOpenedResult>(
    'syberry-today-mark-task-as-opened',
    async taskId => {
      if (!taskId) return { ok: true };

      try {
        await markTaskAsOpened(taskId);

        return { ok: true };
      } catch (error) {
        return { ok: false, error };
      }
    },
  );

  const waitForTaskOpenedRequests = useCallback(async () => {
    await Promise.all(pendingTaskOpenedRequestsRef.current.values());
  }, []);

  const queueMarkTaskAsOpened = useCallback(
    (taskId: SyberryTodayTaskId) => {
      if (pendingTaskOpenedRequestsRef.current.has(taskId)) return;

      const request = markTaskAsOpenedOnce(taskId)
        .then(async result => {
          if (!result.ok) {
            await onMarkTaskAsOpenedError(result.error);
          }
        })
        .finally(() => {
          pendingTaskOpenedRequestsRef.current.delete(taskId);
        });

      pendingTaskOpenedRequestsRef.current.set(taskId, request);
    },
    [markTaskAsOpenedOnce, onMarkTaskAsOpenedError],
  );

  return {
    queueMarkTaskAsOpened,
    waitForTaskOpenedRequests,
  };
};

export default useSyberryTodayTaskOpenedSync;
